import React, { useState, useEffect } from 'react';

function ScheduleModal({ schedule, screens, groups, onSave, onClose }) {
  const [name, setName] = useState('');
  const [targetType, setTargetType] = useState('screen');
  const [screenId, setScreenId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [action, setAction] = useState('tv_on');
  const [scheduleType, setScheduleType] = useState('once');
  const [startTime, setStartTime] = useState('09:00');
  const [specificDate, setSpecificDate] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [params, setParams] = useState({});

  useEffect(() => {
    if (schedule) {
      setName(schedule.name);
      setTargetType(schedule.screen_id ? 'screen' : 'group');
      setScreenId(schedule.screen_id || '');
      setGroupId(schedule.group_id || '');
      setAction(schedule.action);
      setScheduleType(schedule.schedule_type);
      setStartTime(schedule.start_time);
      setSpecificDate(schedule.specific_date || '');
      setDaysOfWeek(schedule.days_of_week ? schedule.days_of_week.split(',') : []);
      setIsActive(schedule.is_active);
      setParams(schedule.params || {});
    } else {
      const today = new Date().toISOString().split('T')[0];
      setSpecificDate(today);
    }
  }, [schedule]);

  const handleDayToggle = (day) => {
    if (daysOfWeek.includes(day)) {
      setDaysOfWeek(daysOfWeek.filter(d => d !== day));
    } else {
      setDaysOfWeek([...daysOfWeek, day].sort());
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      name,
      action,
      schedule_type: scheduleType,
      start_time: startTime,
      is_active: isActive,
      params
    };

    if (targetType === 'screen') {
      data.screen_id = parseInt(screenId);
    } else {
      data.group_id = parseInt(groupId);
    }

    if (scheduleType === 'once') {
      data.specific_date = specificDate;
    } else if (scheduleType === 'weekly') {
      data.days_of_week = daysOfWeek.join(',');
    }

    onSave(data);
  };

  const weekDays = [
    { value: '1', label: 'Lun' },
    { value: '2', label: 'Mar' },
    { value: '3', label: 'Mer' },
    { value: '4', label: 'Jeu' },
    { value: '5', label: 'Ven' },
    { value: '6', label: 'Sam' },
    { value: '7', label: 'Dim' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{schedule ? 'Modifier la programmation' : 'Créer une programmation'}</h2>
          <button onClick={onClose} className="btn-close">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom de la programmation *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Allumage automatique matin"
              required
              autoFocus
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type de cible</label>
              <select value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                <option value="screen">Écran individuel</option>
                <option value="group">Groupe d'écrans</option>
              </select>
            </div>

            <div className="form-group">
              <label>{targetType === 'screen' ? 'Écran' : 'Groupe'} *</label>
              {targetType === 'screen' ? (
                <select value={screenId} onChange={(e) => setScreenId(e.target.value)} required>
                  <option value="">Sélectionner un écran</option>
                  {screens.map(screen => (
                    <option key={screen.id} value={screen.id}>{screen.name}</option>
                  ))}
                </select>
              ) : (
                <select value={groupId} onChange={(e) => setGroupId(e.target.value)} required>
                  <option value="">Sélectionner un groupe</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Action *</label>
            <select value={action} onChange={(e) => setAction(e.target.value)} required>
              <option value="tv_on">Allumer TV</option>
              <option value="tv_off">🌙 Éteindre TV</option>
              <option value="change_url">🌐 Changer URL</option>
              <option value="change_playlist">Changer playlist</option>
            </select>
          </div>

          {action === 'change_url' && (
            <div className="form-group">
              <label>URL *</label>
              <input
                type="url"
                value={params.url || ''}
                onChange={(e) => setParams({ ...params, url: e.target.value })}
                placeholder="https://example.com"
                required
              />
            </div>
          )}

          {action === 'change_playlist' && (
            <div className="form-group">
              <label>ID Playlist *</label>
              <input
                type="number"
                value={params.playlist_id || ''}
                onChange={(e) => setParams({ ...params, playlist_id: parseInt(e.target.value) })}
                placeholder="1"
                required
              />
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Type de programmation *</label>
              <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value)} required>
                <option value="once">Une fois (date spécifique)</option>
                <option value="daily">🔄 Quotidien</option>
                <option value="weekly">📆 Hebdomadaire</option>
              </select>
            </div>

            <div className="form-group">
              <label>Heure *</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
          </div>

          {scheduleType === 'once' && (
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={specificDate}
                onChange={(e) => setSpecificDate(e.target.value)}
                required
              />
            </div>
          )}

          {scheduleType === 'weekly' && (
            <div className="form-group">
              <label>Jours de la semaine *</label>
              <div className="days-selector">
                {weekDays.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    className={`day-btn ${daysOfWeek.includes(day.value) ? 'active' : ''}`}
                    onClick={() => handleDayToggle(day.value)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span>Programmation active</span>
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              {schedule ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ScheduleModal;
